import { FC, useCallback, useContext, useEffect, useState } from "react";

import { Close, FileDownload, Replay } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import {
  ConnectAuditEventModel,
  ConnectImageWithUrl,
  ConnectInvitationModel,
  ConnectLocationPingModel,
  ConnectMeetingModel,
  ConnectRecordingWithRetention,
  getConnectAudit,
  getConnectImages,
  getConnectInvitations,
  getConnectLocations,
  getConnectRecording,
  listConnectRecording,
  postConnectRecordingExport,
} from "@rubi/api-generated/clientRs";
import { toast } from "@rubi/ui";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { userPermissionChecks } from "@/components/UserManagement/utils";
import { AuthStateContext } from "@/contexts/AuthContext";

import { consoleMsgFrom } from "./apiError";
import ParticipantLocationsMap from "./ParticipantLocationsMap";

// Human-readable labels for the audit event types (see connect_audit::event).
const EVENT_LABEL_KEYS: Record<string, string> = {
  meeting_created: "meetingHistory.events.meetingCreated",
  invitation_created: "meetingHistory.events.invitationCreated",
  invitation_sent: "meetingHistory.events.invitationSent",
  link_copied: "meetingHistory.events.linkCopied",
  link_opened: "meetingHistory.events.linkOpened",
  notice_acknowledged: "meetingHistory.events.noticeAcknowledged",
  permissions_updated: "meetingHistory.events.permissionsUpdated",
  waiting_room_entered: "meetingHistory.events.waitingRoomEntered",
  participant_joined: "meetingHistory.events.participantJoined",
  participant_admitted: "meetingHistory.events.participantAdmitted",
  participant_declined: "meetingHistory.events.participantDeclined",
  participant_removed: "meetingHistory.events.participantRemoved",
  recording_started: "meetingHistory.events.recordingStarted",
  recording_stopped: "meetingHistory.events.recordingStopped",
  recording_start_failed: "meetingHistory.events.recordingStartFailed",
  recording_failed: "meetingHistory.events.recordingFailed",
  recording_truncated: "meetingHistory.events.recordingTruncated",
  session_ended: "meetingHistory.events.sessionEnded",
  links_invalidated: "meetingHistory.events.linksInvalidated",
  post_call_message_sent: "meetingHistory.events.postCallMessageSent",
  location_unavailable: "meetingHistory.events.locationUnavailable",
  location_restored: "meetingHistory.events.locationRestored",
  participant_media_changed: "meetingHistory.events.participantMediaChanged",
  camera_requested: "meetingHistory.events.cameraRequested",
  participant_disconnected: "meetingHistory.events.participantDisconnected",
  interviewer_disconnected: "meetingHistory.events.interviewerDisconnected",
  participant_reconnected: "meetingHistory.events.participantReconnected",
  participant_media_off: "meetingHistory.events.participantMediaOff",
  participant_media_on: "meetingHistory.events.participantMediaOn",
  meeting_paused: "meetingHistory.events.meetingPaused",
  meeting_resumed: "meetingHistory.events.meetingResumed",
  pause_requested: "meetingHistory.events.pauseRequested",
  pause_request_cancelled: "meetingHistory.events.pauseRequestCancelled",
  participant_left: "meetingHistory.events.participantLeft",
  export_started: "meetingHistory.events.exportStarted",
  export_succeeded: "meetingHistory.events.exportSucceeded",
  export_failed: "meetingHistory.events.exportFailed",
  re_export_requested: "meetingHistory.events.reExportRequested",
  export_notification_sent: "meetingHistory.events.exportNotificationSent",
  evidence_confirmed: "meetingHistory.events.evidenceConfirmed",
  image_exported: "meetingHistory.events.imageExported",
  image_export_failed: "meetingHistory.events.imageExportFailed",
  media_purged: "meetingHistory.events.mediaPurged",
};

const EXPORT_COLOR: Record<
  string,
  "default" | "success" | "error" | "warning" | "info"
> = {
  successful: "success",
  failed: "error",
  pending: "warning",
  in_progress: "info",
  re_export_requested: "warning",
};

// A queued export is normally claimed within seconds. One still queued after
// this was never picked up, so it stops counting as running and can be retried.
const EXPORT_QUEUE_WINDOW_MS = 10 * 60 * 1000;

const exportQueued = (r: ConnectRecordingWithRetention): boolean =>
  (r.export_status === "pending" ||
    r.export_status === "re_export_requested") &&
  Date.now() - Date.parse(r.time_updated) < EXPORT_QUEUE_WINDOW_MS;

function fmtBytes(t: TFunction, n?: number | null): string {
  if (!n) return "";
  const mb = n / (1024 * 1024);
  return mb >= 1
    ? t("meetingHistory.units.megabytes", { value: mb.toFixed(1) })
    : t("meetingHistory.units.kilobytes", { value: (n / 1024).toFixed(0) });
}

function fmtDuration(t: TFunction, secs?: number | null): string {
  if (!secs) return "";
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return t("meetingHistory.units.duration", {
    minutes: m,
    seconds: String(s).padStart(2, "0"),
  });
}

interface Props {
  open: boolean;
  onClose: () => void;
  orgId: string;
  meeting: ConnectMeetingModel | null;
}

const MeetingHistoryDialog: FC<Props> = ({ open, onClose, orgId, meeting }) => {
  const { t } = useTranslation("connect");
  const { currentUser } = useContext(AuthStateContext);
  const canViewRecordings = userPermissionChecks(
    currentUser?.permissions?.map((p) => p.title) ?? [],
  ).canViewConnectRecordings;
  const meetingId = meeting?.id ?? null;
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState<ConnectAuditEventModel[]>([]);
  const [recordings, setRecordings] = useState<ConnectRecordingWithRetention[]>(
    [],
  );
  const [images, setImages] = useState<ConnectImageWithUrl[]>([]);
  const [invitations, setInvitations] = useState<ConnectInvitationModel[]>([]);
  const [pings, setPings] = useState<ConnectLocationPingModel[]>([]);
  const [exporting, setExporting] = useState<string | null>(null);

  const load = useCallback(
    async (quiet = false) => {
      if (!meetingId) return;
      if (!quiet) setLoading(true);
      try {
        const [aud, rec, inv, loc] = await Promise.all([
          getConnectAudit({ path: { org_id: orgId, meeting_id: meetingId } }),
          listConnectRecording({
            path: { org_id: orgId, meeting_id: meetingId },
          }),
          getConnectInvitations({
            path: { org_id: orgId, meeting_id: meetingId },
          }),
          getConnectLocations({
            path: { org_id: orgId, meeting_id: meetingId },
          }),
        ]);
        setEvents(aud.data?.events ?? []);
        setRecordings(rec.data?.recordings ?? []);
        setInvitations(inv.data?.invitations ?? []);
        setPings(loc.data?.pings ?? []);
        // Images are supplementary (presigning can fail independently): never
        // let them take the audit trail down with them.
        try {
          const img = await getConnectImages({
            path: { org_id: orgId, meeting_id: meetingId },
          });
          setImages(img.data?.images ?? []);
        } catch {
          /* keep the rest of the history visible */
        }
      } catch {
        if (!quiet) toast.error(t("meetingHistory.errors.load"));
      } finally {
        setLoading(false);
      }
    },
    [orgId, meetingId, t],
  );

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // Recording and export finish in the background; follow them while open.
  const settling = recordings.some(
    (r) =>
      r.status === "processing" ||
      r.export_status === "in_progress" ||
      exportQueued(r),
  );
  useEffect(() => {
    if (!open || !settling) return;
    const id = window.setInterval(() => load(true), 15_000);
    return () => window.clearInterval(id);
  }, [open, settling, load]);

  const invById = new Map(invitations.map((i) => [i.id, i]));
  const nameFor = (id?: string | null) => (id && invById.get(id)?.name) || "—";

  // Location points: prefer the latest live sample per participant, else fall
  // back to the invitation's last-known location.
  const latestByInv = new Map<string, ConnectLocationPingModel>();
  for (const p of pings) {
    if (
      p.available &&
      p.latitude != null &&
      p.longitude != null &&
      !latestByInv.has(p.invitation_id)
    ) {
      latestByInv.set(p.invitation_id, p);
    }
  }
  const points =
    latestByInv.size > 0
      ? [...latestByInv.values()].map((p) => ({
          id: p.id,
          name: nameFor(p.invitation_id),
          lat: p.latitude as number,
          lng: p.longitude as number,
        }))
      : invitations
          .filter((i) => i.latitude != null && i.longitude != null)
          .map((i) => ({
            id: i.id,
            name: i.name,
            lat: i.latitude as number,
            lng: i.longitude as number,
          }));

  const reexport = async (recordingId: string) => {
    if (!meetingId) return;
    setExporting(recordingId);
    try {
      await postConnectRecordingExport({
        path: {
          org_id: orgId,
          meeting_id: meetingId,
          recording_id: recordingId,
        },
      });
      toast.success(t("meetingHistory.toasts.reexportRequested"));
      await load();
    } catch (e: unknown) {
      toast.error(consoleMsgFrom(e) ?? t("meetingHistory.errors.reexport"));
    } finally {
      setExporting(null);
    }
  };

  const downloadRecording = async (recordingId: string) => {
    try {
      const res = await getConnectRecording({
        path: { org_id: orgId, recording_id: recordingId },
      });
      if (res.data?.presigned_url) {
        // The URL is served as an attachment, so navigating saves the file and
        // leaves the page in place (a window.open after an await is blocked).
        window.location.assign(res.data.presigned_url);
      } else {
        toast.info(t("meetingHistory.toasts.downloadDisabled"));
      }
    } catch (e: unknown) {
      toast.error(consoleMsgFrom(e) ?? t("meetingHistory.errors.download"));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {meeting?.title
          ? t("meetingHistory.titleWithMeeting", { title: meeting.title })
          : t("meetingHistory.title")}
        {meeting?.occurrence_number && (
          <Typography variant="body2" color="text.secondary">
            {t("meetingHistory.occurrence", {
              number: meeting.occurrence_number,
            })}
          </Typography>
        )}
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Recordings + export status */}
            <Typography variant="subtitle2" gutterBottom>
              {t("meetingHistory.recordings.heading")}
            </Typography>
            {recordings.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t("meetingHistory.recordings.empty")}
              </Typography>
            ) : (
              <Stack spacing={1} sx={{ mb: 2 }}>
                {recordings.map((r) => (
                  <Box
                    key={r.id}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 1.5,
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      flexWrap="wrap"
                    >
                      <Chip
                        size="small"
                        label={t("meetingHistory.recordings.recordingStatus", {
                          status: r.status,
                        })}
                      />
                      <Chip
                        size="small"
                        color={EXPORT_COLOR[r.export_status] ?? "default"}
                        label={t("meetingHistory.recordings.exportStatus", {
                          status: r.export_status,
                        })}
                      />
                      {fmtDuration(t, r.duration_secs) && (
                        <Typography variant="caption" color="text.secondary">
                          {fmtDuration(t, r.duration_secs)} ·{" "}
                          {fmtBytes(t, r.file_size_bytes)}
                        </Typography>
                      )}
                      <Box sx={{ flex: 1 }} />
                      {r.status === "ready" && canViewRecordings && (
                        <Button
                          size="small"
                          startIcon={<FileDownload />}
                          onClick={() => downloadRecording(r.id)}
                        >
                          {t("meetingHistory.recordings.download")}
                        </Button>
                      )}
                      {canViewRecordings &&
                        r.status === "ready" &&
                        r.export_status !== "in_progress" &&
                        !exportQueued(r) && (
                          <Button
                            size="small"
                            startIcon={<Replay />}
                            disabled={exporting === r.id}
                            onClick={() => reexport(r.id)}
                          >
                            {exporting === r.id
                              ? t("meetingHistory.recordings.requesting")
                              : t("meetingHistory.recordings.reexport")}
                          </Button>
                        )}
                    </Stack>
                    {r.evidence_com_item_id && (
                      <Typography variant="caption" color="text.secondary">
                        {r.export_confirmed_at
                          ? t("meetingHistory.recordings.evidenceIdConfirmed", {
                              id: r.evidence_com_item_id,
                              date: new Date(
                                r.export_confirmed_at,
                              ).toLocaleString(),
                            })
                          : t("meetingHistory.recordings.evidenceId", {
                              id: r.evidence_com_item_id,
                            })}
                      </Typography>
                    )}
                    {r.status === "purged" ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        {t("meetingHistory.recordings.purged")}
                      </Typography>
                    ) : (
                      r.purge_after && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          {t("meetingHistory.recordings.purgeOn", {
                            date: new Date(r.purge_after).toLocaleDateString(),
                          })}
                        </Typography>
                      )
                    )}
                    {r.export_error && (
                      <Typography
                        variant="caption"
                        color="error"
                        display="block"
                      >
                        {r.export_error}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            )}
            {/* Officer-captured stills, exported with the recording */}
            {images.length > 0 && (
              <>
                <Typography variant="subtitle2" gutterBottom>
                  {t("meetingHistory.images.heading")}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {images.map((img) => (
                    <Box
                      key={img.id}
                      sx={{
                        width: 132,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        p: 0.75,
                        opacity: img.purged_at ? 0.55 : 1,
                      }}
                    >
                      {img.url && !img.purged_at ? (
                        <Box
                          component="img"
                          src={img.url}
                          alt={img.caption ?? t("meetingHistory.images.alt")}
                          sx={{
                            width: "100%",
                            height: 80,
                            objectFit: "cover",
                            borderRadius: 0.5,
                            display: "block",
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            height: 80,
                            display: "grid",
                            placeItems: "center",
                            bgcolor: "action.hover",
                            borderRadius: 0.5,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {img.purged_at
                              ? t("meetingHistory.images.purged")
                              : t("meetingHistory.images.noPreview")}
                          </Typography>
                        </Box>
                      )}
                      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                        <Chip size="small" label={img.kind} />
                        <Chip
                          size="small"
                          color={EXPORT_COLOR[img.export_status] ?? "default"}
                          label={img.export_status}
                        />
                      </Stack>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        noWrap
                      >
                        {new Date(img.time_created).toLocaleString()}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </>
            )}
            <Divider sx={{ my: 2 }} />

            {/* Audit timeline */}
            <Typography variant="subtitle2" gutterBottom>
              {t("meetingHistory.audit.heading")}
            </Typography>
            {events.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t("meetingHistory.audit.empty")}
              </Typography>
            ) : (
              <Stack spacing={0.5} sx={{ mb: 2 }}>
                {events.map((e) => (
                  <Box
                    key={e.id}
                    sx={{
                      display: "flex",
                      gap: 1,
                      fontSize: 13,
                      alignItems: "baseline",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ minWidth: 150, fontVariantNumeric: "tabular-nums" }}
                    >
                      {new Date(e.time_created).toLocaleString()}
                    </Typography>
                    <span>
                      <strong>
                        {EVENT_LABEL_KEYS[e.event_type]
                          ? t(EVENT_LABEL_KEYS[e.event_type])
                          : e.event_type}
                      </strong>
                      {e.invitation_id && ` — ${nameFor(e.invitation_id)}`}
                      {e.detail && (
                        <span style={{ color: "#888" }}> · {e.detail}</span>
                      )}
                    </span>
                  </Box>
                ))}
              </Stack>
            )}
            <Divider sx={{ my: 2 }} />

            {/* Participant locations */}
            <Typography variant="subtitle2" gutterBottom>
              {t("meetingHistory.locations.heading")}
            </Typography>
            {points.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t("meetingHistory.locations.empty")}
              </Typography>
            ) : (
              <ParticipantLocationsMap points={points} height={280} />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MeetingHistoryDialog;
